import { useCallback, useEffect, useState, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import { FormattedMessage } from 'dibs-react-intl';
import HeartOutlined from 'dibs-icons/exports/legacy/HeartOutlined.ts';
import HeartFilled from 'dibs-icons/exports/legacy/HeartFilled.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import LikeHeartIcon from 'dibs-icons/exports/legacy/LikeHeartIcon.ts';
import { Button } from 'dibs-elements/exports/Button.tsx';

// helpers
import classnames from 'classnames';
import { FOLLOW_DEALER_FLOW, sharedLoginCheck } from '../../../authentication/sharedLoginCheck.ts';
import { type authLabels } from '../../../utils/tracking/shared/followSearchTracking.ts';
import { pageTypeConstants as pageTypes } from '../../../constants/pageTypeConstants.ts';
import { useSharedSaveSearchContext } from '../../../shared/SharedSaveSearchContext/SharedSaveSearchContext.tsx';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

// styles
import dibsCss from 'dibs-css';
import styles from './SaveSearchButton.scss';

import { type SaveSearchButton_itemSearch$key } from './__generated__/SaveSearchButton_itemSearch.graphql.ts';

const parsePageType = (pageType: string | null): string => {
    if (pageType === pageTypes.MORE_FROM_SELLER || pageType === pageTypes.DEALER) {
        return pageTypes.DEALER;
    } else if (pageType === pageTypes.CREATOR) {
        return pageTypes.CREATOR;
    } else {
        return pageTypes.SEARCH;
    }
};

const itemSearchFragment = graphql`
    fragment SaveSearchButton_itemSearch on ItemSearchQueryConnection {
        pageType
    }
`;

type Props = {
    itemSearch: SaveSearchButton_itemSearch$key | null | undefined;
    fullWidth?: boolean;
    locationLabel: (typeof authLabels)[keyof typeof authLabels] | null;
    onComplete?: (isFollowing: boolean) => void;
    size?: 'small' | 'medium' | 'large';
    type?: 'primary' | 'secondary' | 'heart';
};

export const SaveSearchButton: FC<Props> = ({
    itemSearch: itemSearchRef,
    fullWidth = false,
    locationLabel,
    onComplete = () => {},
    size = 'large',
    type,
}) => {
    const { initAutoFollow, isFollowing, favoriteId, favoritesFetched, updateSaveSearch, userId } =
        useSharedSaveSearchContext();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { isMobile, isQuickFiltersRedesignVariant } = useServerVarsContext();

    const [loading, setLoading] = useState(false);
    const [waitForConnection, setWaitForConnection] = useState(false);
    const [autoFollow, setAutoFollow] = useState(initAutoFollow);
    const pageType = itemSearch?.pageType || '';

    const showAuthModal = useCallback(() => {
        sharedLoginCheck({
            onSuccess: () => setWaitForConnection(true),
            onError: () => setLoading(false),
            authOptions: {
                flow: FOLLOW_DEALER_FLOW,
                ga: { label: locationLabel },
            },
        });
    }, [locationLabel]);

    const handleFollow = useCallback(
        async (_userId: string) => {
            await updateSaveSearch({ userId: _userId, onComplete });
            setLoading(false);
        },
        [onComplete, updateSaveSearch]
    );

    const handleClick = useCallback(() => {
        setLoading(true);
        if (userId) {
            handleFollow(userId);
        } else {
            showAuthModal();
        }
    }, [handleFollow, showAuthModal, userId]);

    useEffect(() => {
        /**
         * We must wait for the user fetch before sending off the mutation. We do this by checking
         * whether the favorites connection exists.
         *
         * There's a possibility that the mutation will resolve before the user fetch returns,
         * so the newly returned mutation result will be overridden with whatever comes from the
         * user query.
         */
        if (waitForConnection && userId && favoritesFetched) {
            setWaitForConnection(false);
            handleFollow(userId);
        }

        /**
         * If autoFollow is true, when the component renders it should try and follow the search automatically.
         * We need to consider both situations when the user is and is not logged in.
         *
         * Case 1:
         *
         * User is logged in, so userId is present in props.  If this is the case we need to make sure
         * favoritesConnection is populated (this information is asynced in).  Once we have this information
         * we check to make sure the user is already not following the search.
         * If they are not we trigger the click event on the button to handle the following logic.
         */
        if (autoFollow && favoritesFetched) {
            setAutoFollow(false);
            if (!favoriteId) {
                handleClick();
            }
        }

        /**
         * Case 2:
         *
         * In this case the user is not logged in so we trigger the handleClick function to follow the search.
         * At this point we need to know the user is not logged in, even though we make this check in the handleClick function,
         * because if the user is logged in we need to make sure favoritesConnection is populated (see Case 1)
         *
         */
        if (autoFollow && !userId) {
            setAutoFollow(false);
            handleClick();
        }
    }, [
        autoFollow,
        favoriteId,
        favoritesFetched,
        handleClick,
        handleFollow,
        userId,
        waitForConnection,
    ]);

    const parsedPageType = parsePageType(pageType);

    const circleFrostedTheme = isMobile ? 'circle-frosted-medium' : 'circle-frosted-small';

    return type === 'heart' ? (
        <Link
            className={styles.heartLink}
            onClick={handleClick}
            dataTn="mobile-item-actions-follow-search"
        >
            <LikeHeartIcon
                className={classnames(
                    dibsCss.h18px,
                    dibsCss.w18px,
                    dibsCss.ptXxsmall,
                    styles.heartIcon
                )}
                isFilled={isFollowing || loading}
                animateHeartBeat={loading}
                theme={isQuickFiltersRedesignVariant ? circleFrostedTheme : 'dark'}
            />
        </Link>
    ) : (
        <Button
            className={classnames(styles.button, {
                [dibsCss.wFull]: fullWidth,
                [styles.isQuickFiltersRedesignVariant]: isQuickFiltersRedesignVariant && !isMobile,
            })}
            disabled={loading}
            size={size}
            style={isQuickFiltersRedesignVariant ? 'circle' : undefined}
            fullWidth={fullWidth}
            onClick={handleClick}
            dataTn="item-actions-follow-search"
            type={
                // after quick_filters_redesign_variant is concluded, update logic so type would be passed into this component and not dependent on both size and type
                isQuickFiltersRedesignVariant || (size !== 'large' && type !== 'primary')
                    ? 'secondary'
                    : 'primary'
            }
        >
            <div
                className={classnames(dibsCss.flex, dibsCss.justifyCenter, dibsCss.itemsCenter, {
                    [dibsCss.textBodySmallHeavy]: isQuickFiltersRedesignVariant,
                })}
            >
                {isQuickFiltersRedesignVariant && (
                    <LikeHeartIcon
                        className={classnames(styles.heartIcon, dibsCss.mrXxsmall)}
                        isFilled={isFollowing || loading}
                        animateHeartBeat={loading}
                        theme="circle-frosted-small"
                    />
                )}
                {!isQuickFiltersRedesignVariant &&
                    (isFollowing ? (
                        <HeartFilled
                            className={classnames(dibsCss.h14px, dibsCss.w14px, dibsCss.mrXsmall)}
                        />
                    ) : (
                        <HeartOutlined
                            className={classnames(
                                dibsCss.h14px,
                                dibsCss.w14px,
                                dibsCss.mrXsmall,
                                styles.button
                            )}
                        />
                    ))}
                {isFollowing ? (
                    <FormattedMessage
                        id="shared.SaveSearchButton.savedSearch"
                        defaultMessage="{pageType, select,
                    seller {Saved Seller}
                    creator {Saved Creator}
                    other {Saved Search}
                }"
                        values={{ pageType: parsedPageType }}
                    />
                ) : (
                    <FormattedMessage
                        id="shared.SaveSearchButton.saveSearch"
                        defaultMessage="{pageType, select,
                    seller {Save Seller}
                    creator {Save Creator}
                    other {Save Search}
                }"
                        values={{ pageType: parsedPageType }}
                    />
                )}
            </div>
        </Button>
    );
};
